/**
 * AdminJourneyImages — preview & regenerate the 105 UK Driving Theory
 * Day 3-21 card images. Click Regenerate on any card whose image
 * doesn't match the text — backend re-runs Gemini Nano Banana with
 * the hand-engineered per-card prompt and updates the DB.
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import { Loader2, RefreshCw, AlertCircle, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

export default function AdminJourneyImages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState({}); // { [day-kind-id]: true }
  const [filter, setFilter] = useState("all"); // all | missing | has

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/journey-images/list`, { headers: auth() });
      const data = await res.json();
      setCards(data.cards || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchList();
  }, [user]);

  if (!user || user.role !== "admin") return null;

  const handleRegen = async (card) => {
    const key = `${card.day}-${card.kind}-${card.card_id}`;
    setRegen((r) => ({ ...r, [key]: true }));
    try {
      const res = await fetch(`${API}/api/admin/journey-images/regenerate`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ day: card.day, kind: card.kind, card_id: card.card_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || res.statusText);
      // Update inline + force-refresh image src to bypass browser cache
      setCards((cs) => cs.map((c) =>
        c.day === card.day && c.kind === card.kind && c.card_id === card.card_id
          ? { ...c, image_url: data.image_url + "?t=" + Date.now() }
          : c
      ));
    } catch (e) {
      alert(`Regen failed: ${e.message}`);
    } finally {
      setRegen((r) => { const n = { ...r }; delete n[key]; return n; });
    }
  };

  const filteredCards = cards.filter((c) => {
    if (filter === "missing") return !c.image_url;
    if (filter === "has") return !!c.image_url;
    return true;
  });

  const grouped = {};
  filteredCards.forEach((c) => {
    grouped[c.day] = grouped[c.day] || { theme: c.theme, items: [] };
    grouped[c.day].items.push(c);
  });

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-journey-images-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-[#292524]">Journey Card Images</h2>
            <p className="text-sm text-[#78716C]">
              {cards.length} cards in UK Driving Theory Day 3-21 ·{" "}
              {cards.filter((c) => c.image_url).length} have images
            </p>
          </div>
          <div className="flex items-center gap-2">
            {["all", "has", "missing"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  filter === f
                    ? "bg-[#0D1B2A] text-white"
                    : "bg-white border border-[#E6E2DD] text-[#78716C]"
                }`}
                data-testid={`filter-${f}`}
              >
                {f === "all" ? "All" : f === "has" ? "Has image" : "Missing"}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-[#78716C]">
            <Loader2 className="inline-block animate-spin" /> Loading cards…
          </div>
        )}

        {Object.entries(grouped).map(([day, info]) => (
          <div key={day} className="space-y-2">
            <h3 className="font-heading text-base text-[#292524] mt-6">
              Day {day} — <span className="text-[#78716C] font-normal italic">{info.theme}</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {info.items.map((card) => {
                const key = `${card.day}-${card.kind}-${card.card_id}`;
                const isRegen = !!regen[key];
                return (
                  <div
                    key={key}
                    className="border border-[#E6E2DD] rounded-lg overflow-hidden bg-white"
                    data-testid={`journey-image-card-${key}`}
                  >
                    <div className="relative aspect-video bg-[#0D1B2A]">
                      {card.image_url ? (
                        <img
                          src={card.image_url.startsWith("http") ? card.image_url : `${API}${card.image_url}`}
                          alt={card.front}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <ImageIcon size={28} />
                          <span className="text-xs mt-2">No image</span>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] uppercase tracking-wider">
                        {card.kind} · {card.card_id}
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-[#292524] leading-snug">{card.front}</p>
                      <p className="text-xs text-[#78716C] leading-snug mt-1">{card.back}</p>
                      <button
                        onClick={() => handleRegen(card)}
                        disabled={isRegen}
                        className="mt-3 text-xs px-3 py-1.5 rounded-md border border-[#E6E2DD] hover:bg-[#FAF9F7] disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5"
                        data-testid={`regen-btn-${key}`}
                      >
                        {isRegen ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <RefreshCw size={12} />
                        )}
                        {isRegen ? "Generating…" : "Regenerate"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
